import type {ReactElement} from 'react';
import React, {useRef, useEffect, useState} from 'react';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {Text, TextSize, TextType} from '@Components/text';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import type {SelectableAiThumbnailItemProps} from './selectable-ai-thumbnail-item.types';
import styles from './selectable-ai-thumbnail-item.module.scss';

const IS_MOBILE = false;

export function SelectableAiThumbnailItem({isSelected = false, isDisabled = false, isSmall = false, ...props}: SelectableAiThumbnailItemProps): ReactElement {
  const [loaded, setLoaded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const itemRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    const handleTouchOrClick = (): void => {
      if (showTooltip) {
        setShowTooltip(false);
      }
    };

    window.addEventListener('touchstart', handleTouchOrClick);

    return () => {
      window.removeEventListener('touchstart', handleTouchOrClick);
    };
  }, [showTooltip]);

  const triggerTooltip = (): void => {
    setShowTooltip(true);
    setTimeout(() => {
      return setShowTooltip(false);
    }, 3000);
  };

  const getOverlayContainer = (): ReactElement | null => {
    return props.overlayText || props.overlayItem ? (
      <div className={`spacing-m-1 radius-8 ${styles.overlayTextContainer}`}>
        {props.overlayText ? <Text type={TextType.BODY} bold size={TextSize.XXSMALL} val={props.overlayText} className={`content-body-white ${styles.overlayText}`} /> : null}
        {props.overlayItem ?? null}
      </div>
    ) : null;
  };

  const getText = (): ReactElement | null => {
    return props.text ? <Text bold={isSelected} className={styles.text} val={props.text} size={TextSize.XXSMALL} /> : null;
  };

  const getImageContainer = (): ReactElement => {
    return (
      <div className={`flex-center hover-transition-all radius-12 ${styles.imageContainer} ${isSmall ? styles.smallItem : ''}`}>
        <img
          onLoad={() => {
            return setLoaded(true);
          }}
          loading="lazy"
          className={`radius-8 ${styles.image} ${props.imageClasses ?? ''} ${isSmall ? styles.smallItem : ''}`}
          src={props.thumbnailImage}
          alt={props.text}
        />
        {!loaded ? <SkeletonBrick className={`_full-height ${styles.loadingSkeleton}`} /> : null}
        {getOverlayContainer()}
        {isDisabled ? <div className={`radius-8 ${styles.disabledOverlay}`} /> : null}
      </div>
    );
  };

  const handleItemClick = (e: React.MouseEvent<HTMLLIElement>): void => {
    if (!isDisabled) {
      props.onClick(props.id, e);
    } else if (IS_MOBILE && itemRef.current) {
      itemRef.current.scrollIntoView();
      triggerTooltip();
    }
  };

  const getItemClasses = (): string => {
    return `spacing-p-0 _cursor-pointer flex-v-row flex-center radius-4 flex-shrink-0 ${styles.filterListItem} ${isSelected ? styles.selectedItem : ''} ${isSmall ? styles.smallItem : ''} ${isDisabled ? styles.disabled : ''}`;
  };

  const renderItem = (): ReactElement => {
    return (
      <li
        ref={itemRef}
        className={getItemClasses()}
        onClick={handleItemClick}
        onMouseEnter={() => {
          if (!IS_MOBILE) {
            triggerTooltip();
          }
        }}
        onMouseLeave={() => {
          if (!IS_MOBILE) {
            setShowTooltip(false);
          }
        }}
      >
        {getImageContainer()}
        {getText()}
      </li>
    );
  };

  const renderDisabledItem = (): ReactElement => {
    return (
      <OverlayTrigger
        flip
        placement="bottom"
        show={showTooltip}
        overlay={
          <Tooltip arrowProps={{}} className="tempshitjs">
            {window.i18next.t('pmwjs_subtitles_already_generated')}
          </Tooltip>
        }
        offset={[0, -135]}
      >
        {renderItem()}
      </OverlayTrigger>
    );
  };

  return isDisabled ? renderDisabledItem() : renderItem();
}
